/**
 * Support workspace API (FOR-350). The owner uses /api/support/admin; a project
 * member (docs/support-api.md §12.12) reads and replies through
 * /api/support/member, which the API scopes to their own projects. Customer
 * messages are in here, so nothing is persisted to localStorage
 * (meta.persist false). apiClient already returns the response body.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/lib/api"
import { useAuth } from "@/hooks/useAuth"
import { isOwner } from "@/lib/roles"
import {
  listQuery,
  type SupportBlock,
  type SupportBlockType,
  type SupportContext,
  type SupportConversation,
  type SupportConversationList,
  type SupportConversationRow,
  type SupportCounts,
  type SupportListFilters,
  type SupportMessage,
  type SupportMode,
  type SupportProject,
  type SupportProjectCreated,
  type SupportProjectInput,
  type SupportSecret,
  type SupportOutbox,
  type SupportSnippet,
  type SupportStats,
  type SupportStatus,
} from "@/lib/support"

const BASE = "/support/admin"

/** Who is looking: the owner (everything) or a project member (their projects, read and reply). */
export type SupportRole = "owner" | "member"

/** The inbox routes for a role; settings, notes, status, spam and tasks stay on the owner's. */
export function inboxBase(role: SupportRole): string {
  return role === "member" ? "/support/member" : BASE
}

/** The signed-in person's Support role, or null when they have no Support access. */
export function useSupportRole(): SupportRole | null {
  const { user } = useAuth()
  if (isOwner(user)) return "owner"
  return user?.support_member ? "member" : null
}

/** Someone who can be assigned a conversation in a project (the owner or a member). */
export interface SupportAssignee {
  id: number
  name: string
  email: string
  role: "owner" | "member"
}

export interface SupportMember {
  user_id: number
  name: string | null
  email: string | null
  granted_at: string | null
}

/** What /support/member/projects returns: no settings, secrets or addresses. */
interface MemberProject {
  slug: string
  name: string
  brand_color: string | null
  mode: SupportMode
  logo_url: string | null
  unread: number
  waiting_on_team: number
}

export interface SupportConversationUpdate {
  status?: SupportStatus
  assignee_id?: number | null
  unread?: boolean
  spam?: boolean
  /** With spam: true, also block the sender's address or whole domain. */
  block?: "email" | "domain"
}

export const supportApi = {
  projects: (role: SupportRole = "owner"): Promise<SupportProject[]> =>
    role === "member"
      ? apiClient.get<{ data: MemberProject[] }>(`/support/member/projects`).then((b) => b.data.map(fromMemberProject))
      : apiClient.get<{ data: SupportProject[] }>(`${BASE}/projects`).then((b) => b.data),
  members: (slug: string) => apiClient.get<{ data: SupportMember[] }>(`${BASE}/projects/${encodeURIComponent(slug)}/members`).then((b) => b.data),
  addMember: (slug: string, userId: number) =>
    apiClient.post<{ data: SupportMember[] }>(`${BASE}/projects/${encodeURIComponent(slug)}/members`, { user_id: userId }).then((b) => b.data),
  removeMember: (slug: string, userId: number) =>
    apiClient.delete<{ data: SupportMember[] }>(`${BASE}/projects/${encodeURIComponent(slug)}/members/${userId}`).then((b) => b.data),
  assignees: (slug: string) => apiClient.get<{ data: SupportAssignee[] }>(`${BASE}/projects/${encodeURIComponent(slug)}/assignees`).then((b) => b.data),
  createProject: (body: SupportProjectInput & { slug: string; name: string }) => apiClient.post<SupportProjectCreated>(`${BASE}/projects`, body),
  updateProject: (slug: string, body: SupportProjectInput) =>
    apiClient.patch<{ data: SupportProject }>(`${BASE}/projects/${encodeURIComponent(slug)}`, body).then((b) => b.data),
  /** The new value comes back once; the old one keeps working for 24 hours. */
  rotate: (slug: string, which: SupportSecret) =>
    apiClient.post<Partial<Record<SupportSecret, string>>>(`${BASE}/projects/${encodeURIComponent(slug)}/rotate`, { which }).then((b) => b[which] ?? ""),
  setMode: (slug: string, mode: SupportMode) =>
    apiClient
      .post<{ data: SupportProject }>(`${BASE}/projects/${encodeURIComponent(slug)}/mode`, mode === "live" ? { mode, confirm: true } : { mode })
      .then((b) => b.data),

  /** Sets (or with null clears) the project's own Resend key. Write-only. */
  setResendKey: (slug: string, resendKey: string | null) =>
    apiClient.put(`${BASE}/projects/${encodeURIComponent(slug)}/resend-key`, { resend_key: resendKey }),

  conversations: (filters: SupportListFilters, role: SupportRole = "owner") =>
    apiClient.get<SupportConversationList>(`${inboxBase(role)}/conversations`, { params: listQuery(filters) }),
  counts: (role: SupportRole = "owner") => apiClient.get<{ data: SupportCounts }>(`${inboxBase(role)}/counts`).then((b) => b.data),
  /** Opening a conversation marks it read. */
  conversation: (id: string, role: SupportRole = "owner") =>
    apiClient.get<{ data: SupportConversation }>(`${inboxBase(role)}/conversations/${encodeURIComponent(id)}`).then((b) => b.data),
  update: (id: string, body: SupportConversationUpdate) =>
    apiClient.patch<{ data: SupportConversationRow }>(`${BASE}/conversations/${encodeURIComponent(id)}`, body).then((b) => b.data),
  /**
   * A text reply with undoSeconds is held on the server (an outbox row) and sent when the window
   * passes; replies with files go straight out.
   */
  reply: (id: string, body: string, status?: SupportStatus, files: File[] = [], role: SupportRole = "owner", undoSeconds = 0): Promise<SupportMessage | { outbox: SupportOutbox }> => {
    const url = `${inboxBase(role)}/conversations/${encodeURIComponent(id)}/reply`
    if (files.length === 0) {
      return apiClient
        .post<{ data: SupportMessage | { outbox: SupportOutbox } }>(url, { body, ...(status ? { status } : {}), ...(undoSeconds > 0 ? { undo_seconds: undoSeconds } : {}) })
        .then((b) => b.data)
    }
    const form = new FormData()
    form.append("body", body)
    if (status) form.append("status", status)
    for (const file of files) form.append("files[]", file)
    return apiClient.post<{ data: SupportMessage }>(url, form, { headers: { "Content-Type": "multipart/form-data" } }).then((b) => b.data)
  },
  note: (id: string, body: string) =>
    apiClient.post<{ data: SupportMessage }>(`${BASE}/conversations/${encodeURIComponent(id)}/note`, { body }).then((b) => b.data),
  context: (id: string) => apiClient.get<{ data: SupportContext }>(`${BASE}/conversations/${encodeURIComponent(id)}/context`).then((b) => b.data),
  createTask: (id: string, body: { title?: string; board?: string } = {}) =>
    apiClient
      .post<{ data: { task: { id: number; key: string } } }>(`${BASE}/conversations/${encodeURIComponent(id)}/task`, body)
      .then((b) => b.data.task),
  /**
   * An attachment's bytes (or its thumbnail) as a Blob. Always fetched as a download with the session and
   * previewed from a blob URL, so nothing renders on the API's origin.
   */
  attachmentBlob: (id: string, role: SupportRole = "owner", variant?: "thumb") =>
    apiClient.get<Blob>(`${inboxBase(role)}/attachments/${encodeURIComponent(id)}`, { responseType: "blob", params: variant ? { variant } : {} }),
  /** A direct-upload video's short-lived storage link (a <video> plays it without a cross-origin fetch). */
  attachmentLink: (id: string, role: SupportRole = "owner") =>
    apiClient.get<{ url: string; expires_at: string }>(`${inboxBase(role)}/attachments/${encodeURIComponent(id)}`, { params: { link: 1 } }),
  /** Fetches with the session (the route needs it) and hands the file to the browser. */
  downloadAttachment: async (id: string, filename: string, role: SupportRole = "owner", direct = false) => {
    if (direct) {
      const { url } = await supportApi.attachmentLink(id, role)
      window.open(url, "_blank", "noopener,noreferrer")
      return
    }
    const blob = await apiClient.get<Blob>(`${inboxBase(role)}/attachments/${encodeURIComponent(id)}`, { responseType: "blob" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },

  /** Cancel a held reply; resolves with its text so the composer can put it back. */
  cancelOutbox: (id: string, outboxId: number, role: SupportRole = "owner") =>
    apiClient.delete<{ data: SupportOutbox; body: string }>(`${inboxBase(role)}/conversations/${encodeURIComponent(id)}/outbox/${outboxId}`),

  /** Members get the global saved replies and their own products'. */
  snippets: (project?: string, role: SupportRole = "owner") =>
    apiClient.get<{ data: SupportSnippet[] }>(`${inboxBase(role)}/snippets`, { params: project ? { project } : {} }).then((b) => b.data),
  createSnippet: (body: { project: string | null; title: string; body: string }) =>
    apiClient.post<{ data: SupportSnippet }>(`${BASE}/snippets`, body).then((b) => b.data),
  updateSnippet: (id: number, body: Partial<{ project: string | null; title: string; body: string }>) =>
    apiClient.patch<{ data: SupportSnippet }>(`${BASE}/snippets/${id}`, body).then((b) => b.data),
  deleteSnippet: (id: number) => apiClient.delete(`${BASE}/snippets/${id}`),
  useSnippet: (id: number, role: SupportRole = "owner") => apiClient.post(`${inboxBase(role)}/snippets/${id}/use`, {}),

  stats: (project: string, days: number) =>
    apiClient.get<{ data: SupportStats }>(`${BASE}/stats`, { params: { ...(project ? { project } : {}), days } }).then((b) => b.data),

  blocks: () => apiClient.get<{ data: SupportBlock[] }>(`${BASE}/blocks`).then((b) => b.data),
  addBlock: (body: { type: SupportBlockType; value: string; reason?: string; project_id?: number | null }) =>
    apiClient.post<{ data: SupportBlock }>(`${BASE}/blocks`, body).then((b) => b.data),
  removeBlock: (id: number) => apiClient.delete(`${BASE}/blocks/${id}`),
}

function fromMemberProject(p: MemberProject): SupportProject {
  return {
    id: 0, slug: p.slug, name: p.name, persona_name: null, brand_color: p.brand_color, mode: p.mode,
    sending_domain: null, from_local: "", inbound_domain: null, hosted_domain: null, app_url: null, allowed_origins: null,
    test_recipients: null, context_url: null, webhook_url: null, feedback_board_id: null, server_key_prefix: null,
    settings: { uploads: true, max_upload_mb: 10, feedback_categories: [], alert_on_bug: false, include_in_daily_summary: false, sandbox_alerts: false, logo_url: p.logo_url },
    from: null, live_at: null, waiting_on_team: p.waiting_on_team, unread: p.unread,
  }
}

export const supportKeys = {
  all: ["support"] as const,
  projects: ["support", "projects"] as const,
  lists: ["support", "conversations"] as const,
  list: (filters: SupportListFilters) => ["support", "conversations", filters] as const,
  counts: ["support", "counts"] as const,
  members: (slug: string) => ["support", "members", slug] as const,
  assignees: (slug: string) => ["support", "assignees", slug] as const,
  conversation: (id: string) => ["support", "conversation", id] as const,
  context: (id: string) => ["support", "context", id] as const,
  blocks: ["support", "blocks"] as const,
  snippets: (project?: string) => ["support", "snippets", project ?? ""] as const,
  stats: (project: string, days: number) => ["support", "stats", project, days] as const,
}

/** Nav badges and the open list look again every minute. */
const POLL_MS = 60_000

// A person is either the owner or a member, never both, so the query keys need no role.

export function useSupportCounts(enabled = true) {
  const role = useSupportRole()
  // The nav badges and the workspace switcher share this one query: every minute and on focus.
  return useQuery({
    queryKey: supportKeys.counts,
    queryFn: () => supportApi.counts(role!),
    enabled: enabled && !!role,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    meta: { persist: false },
  })
}

export function useSupportProjects(enabled = true) {
  const role = useSupportRole()
  return useQuery({ queryKey: supportKeys.projects, queryFn: () => supportApi.projects(role!), enabled: enabled && !!role, staleTime: 30_000, meta: { persist: false } })
}

/** The people on a project (owner only). */
export function useSupportMembers(slug: string) {
  return useQuery({ queryKey: supportKeys.members(slug), queryFn: () => supportApi.members(slug), meta: { persist: false } })
}

/** Who a conversation in this project can be assigned to (owner only). */
export function useSupportAssignees(slug: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: supportKeys.assignees(slug ?? ""),
    queryFn: () => supportApi.assignees(slug!),
    enabled: enabled && !!slug,
    staleTime: 60_000,
    meta: { persist: false },
  })
}

export function useSupportMemberMutations(slug: string) {
  const qc = useQueryClient()
  const done = (list: SupportMember[]) => {
    qc.setQueryData(supportKeys.members(slug), list)
    qc.invalidateQueries({ queryKey: supportKeys.assignees(slug) })
  }
  return {
    add: useMutation({ mutationFn: (userId: number) => supportApi.addMember(slug, userId), onSuccess: done }),
    remove: useMutation({ mutationFn: (userId: number) => supportApi.removeMember(slug, userId), onSuccess: done }),
  }
}

export function useSupportConversations(filters: SupportListFilters) {
  const role = useSupportRole()
  return useQuery({
    queryKey: supportKeys.list(filters),
    queryFn: () => supportApi.conversations(filters, role!),
    enabled: !!role,
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
    meta: { persist: false },
  })
}

export function useSupportConversation(id: string | undefined) {
  const qc = useQueryClient()
  const role = useSupportRole()
  return useQuery({
    queryKey: supportKeys.conversation(id ?? ""),
    queryFn: async () => {
      const data = await supportApi.conversation(id!, role!)
      // The read cleared its unread flag: when a cached list still shows it
      // unread, the list dot and the nav badge follow (not on every poll).
      const shownUnread = qc
        .getQueriesData<SupportConversationList>({ queryKey: supportKeys.lists })
        .some(([, list]) => list?.data.some((row) => row.id === id && row.unread))
      if (shownUnread) {
        qc.invalidateQueries({ queryKey: supportKeys.lists })
        qc.invalidateQueries({ queryKey: supportKeys.counts })
      }
      return data
    },
    enabled: !!id && !!role,
    refetchInterval: POLL_MS,
    meta: { persist: false },
  })
}

/** The product's org panel: fetched only when the panel asks (it calls the product). */
export function useSupportContext(id: string, enabled: boolean) {
  return useQuery({
    queryKey: supportKeys.context(id),
    queryFn: () => supportApi.context(id),
    enabled,
    // The API caches it for five minutes too.
    staleTime: 5 * 60_000,
    retry: false,
    meta: { persist: false },
  })
}

export function useSupportBlocks() {
  return useQuery({ queryKey: supportKeys.blocks, queryFn: supportApi.blocks, meta: { persist: false } })
}

/** Writes on one conversation; each refreshes the thread, the lists and the badges. */
export function useConversationMutations(id: string) {
  const qc = useQueryClient()
  const role = useSupportRole()
  const refresh = () => {
    qc.invalidateQueries({ queryKey: supportKeys.conversation(id) })
    qc.invalidateQueries({ queryKey: supportKeys.lists })
    qc.invalidateQueries({ queryKey: supportKeys.counts })
    qc.invalidateQueries({ queryKey: supportKeys.projects })
  }
  return {
    update: useMutation({
      mutationFn: (body: SupportConversationUpdate) => supportApi.update(id, body),
      onSuccess: (_row, body) => {
        refresh()
        if (body.block) qc.invalidateQueries({ queryKey: supportKeys.blocks })
      },
    }),
    reply: useMutation({
      mutationFn: (v: { body: string; status?: SupportStatus; files?: File[]; undoSeconds?: number }) => supportApi.reply(id, v.body, v.status, v.files, role ?? "owner", v.undoSeconds),
      onSuccess: refresh,
    }),
    cancelOutbox: useMutation({ mutationFn: (outboxId: number) => supportApi.cancelOutbox(id, outboxId, role ?? "owner"), onSuccess: refresh }),
    /** After a held reply's window: show it on the thread. */
    refresh,
    note: useMutation({ mutationFn: (body: string) => supportApi.note(id, body), onSuccess: refresh }),
    createTask: useMutation({ mutationFn: (body: { title?: string; board?: string }) => supportApi.createTask(id, body), onSuccess: refresh }),
  }
}

export function useSupportProjectMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: supportKeys.projects })
  return {
    create: useMutation({ mutationFn: supportApi.createProject, onSuccess: refresh }),
    update: useMutation({ mutationFn: (v: { slug: string; body: SupportProjectInput }) => supportApi.updateProject(v.slug, v.body), onSuccess: refresh }),
    setResendKey: useMutation({ mutationFn: (v: { slug: string; key: string | null }) => supportApi.setResendKey(v.slug, v.key), onSuccess: refresh }),
    rotate: useMutation({ mutationFn: (v: { slug: string; which: SupportSecret }) => supportApi.rotate(v.slug, v.which) }),
    setMode: useMutation({
      mutationFn: (v: { slug: string; mode: SupportMode }) => supportApi.setMode(v.slug, v.mode),
      onSuccess: () => {
        refresh()
        // Open threads show the sandbox banner from the project's mode.
        qc.invalidateQueries({ queryKey: supportKeys.all })
      },
    }),
  }
}

export function useSupportBlockMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: supportKeys.blocks })
  return {
    add: useMutation({ mutationFn: supportApi.addBlock, onSuccess: refresh }),
    remove: useMutation({ mutationFn: supportApi.removeBlock, onSuccess: refresh }),
  }
}

export function useSupportSnippets(project?: string, enabled = true) {
  const role = useSupportRole()
  return useQuery({
    queryKey: supportKeys.snippets(project),
    queryFn: () => supportApi.snippets(project, role!),
    enabled: enabled && !!role,
    staleTime: 60_000,
    meta: { persist: false },
  })
}

export function useSupportSnippetMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ["support", "snippets"] })
  return {
    create: useMutation({ mutationFn: supportApi.createSnippet, onSuccess: refresh }),
    update: useMutation({ mutationFn: (v: { id: number; body: Partial<{ project: string | null; title: string; body: string }> }) => supportApi.updateSnippet(v.id, v.body), onSuccess: refresh }),
    remove: useMutation({ mutationFn: supportApi.deleteSnippet, onSuccess: refresh }),
  }
}

export function useSupportStats(project: string, days: number) {
  return useQuery({ queryKey: supportKeys.stats(project, days), queryFn: () => supportApi.stats(project, days), meta: { persist: false } })
}
